import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import { DeviceKind, DeviceOutcome } from '../core';
import { acquireDevicesThunk } from './acquireDevices.thunk';

/**
 * The cohesive device-acquisition state: the richer {@link DeviceOutcome} per kind
 * (the full taxonomy the legacy `user.microphonePermissionState` could not express)
 * plus whether an acquisition is currently in flight.
 *
 * `isAcquiring` is what makes the "Click Allow" overlay *reactive* — it is true only
 * while a `getUserMedia` request is actually pending, rather than being inferred from
 * a permission state of `'prompt'` (the optimistic read that leaves the overlay stuck
 * when no prompt ever appears).
 */
export type DevicesState = {
  microphone: DeviceOutcome | null;
  camera: DeviceOutcome | null;
  isAcquiring: boolean;
  /**
   * Whether the browser sees a microphone / a camera, read from a raw `enumerateDevices()`
   * by `kind`. Per-kind on purpose: the gate needs BOTH (so we warn when either is missing),
   * but we still acquire and show whichever IS present (we don't withhold the prompt or the
   * one working device just because the other is absent). Unlike the enumerated device
   * *list*, this is accurate before permission is granted (pre-permission entries have blank
   * deviceId/label and are filtered out of the list). `null` until first checked.
   */
  hasMicrophone: boolean | null;
  hasCamera: boolean | null;
};

export const initialDevicesState: DevicesState = {
  microphone: null,
  camera: null,
  isAcquiring: false,
  hasMicrophone: null,
  hasCamera: null,
};

export const devicesSlice = createSlice({
  name: 'devices',
  initialState: initialDevicesState,
  reducers: {
    outcomeReset: (state, { payload }: PayloadAction<DeviceKind | undefined>) => {
      if (!payload || payload === 'microphone') state.microphone = null;
      if (!payload || payload === 'camera') state.camera = null;
    },
    inputDevicePresenceChanged: (state, { payload }: PayloadAction<{ microphone: boolean; camera: boolean }>) => {
      state.hasMicrophone = payload.microphone;
      state.hasCamera = payload.camera;
    },
  },
  // `isAcquiring` is driven by the thunk lifecycle rather than manual start/settle
  // actions, so it can never get stuck `true` no matter how the thunk ends (a throw
  // still hits `rejected`). The overlay that keys off it is therefore always cleared.
  extraReducers: (builder) => {
    builder
      .addCase(acquireDevicesThunk.pending, (state) => {
        state.isAcquiring = true;
      })
      .addCase(acquireDevicesThunk.fulfilled, (state, { payload }) => {
        state.isAcquiring = false;
        if (payload.microphone !== undefined) state.microphone = payload.microphone;
        if (payload.camera !== undefined) state.camera = payload.camera;
      })
      .addCase(acquireDevicesThunk.rejected, (state) => {
        state.isAcquiring = false;
      });
  },
});

export const devicesReducer = devicesSlice.reducer;
export const { outcomeReset, inputDevicePresenceChanged } = devicesSlice.actions;
